<template>
    <v-dialog
        :value="value"
        @input="onDialogInput"
        max-width="90%"
        content-class="ticket-detail-dialog"
        transition="dialog-transition"
    >
        <v-card class="ticket-detail-dialog-card">
            <v-toolbar dense flat>
                <v-toolbar-title class="text-subtitle-1">
                    {{ $t('ticket.headers.id') }} {{ ticketDisplayKey }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <template>
                    <v-btn
                        v-if="showSaveButton && ticket && ticket.id_ticket"
                        small
                        depressed
                        color="primary"
                        class="mr-2"
                        :disabled="!hasPendingChanges || saving"
                        :loading="saving"
                        @click="$emit('save')"
                    >
                        <v-icon left small>mdi-content-save</v-icon>
                        {{ $t('ticket.saveChanges') }}
                    </v-btn>
                    <v-btn
                        v-if="showPrintButton && ticket && ticket.id_ticket"
                        small
                        outlined
                        color="primary"
                        class="mr-2"
                        @click="$emit('print-ticket', ticket)"
                    >
                        <v-icon left small>mdi-printer</v-icon>
                        {{ $t('ticket.print.printTicket') }}
                    </v-btn>
                    <v-btn
                        v-if="showConcludeTicketButton && ticket && ticket.id_ticket && !isTicketClosed"
                        small
                        outlined
                        color="success"
                        class="mr-2"
                        @click="emitRequestFinalizeTicket('conclude')"
                    >
                        <v-icon left small>mdi-check-circle</v-icon>
                        {{ $t('ticket.concludeTicket') }}
                    </v-btn>
                    <v-btn
                        v-if="showCloseTicketButton && ticket && ticket.id_ticket && !isTicketClosed"
                        small
                        outlined
                        color="error"
                        class="mr-2"
                        @click="emitRequestFinalizeTicket('cancel')"
                    >
                        <v-icon left small>mdi-close-circle-outline</v-icon>
                        {{ $t('ticket.cancelTicket') }}
                    </v-btn>
                </template>
                <v-btn icon small @click="requestClose">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text class="pa-3 overflow-y-auto dialog-content" v-if="ticket && ticket.id_ticket">
                <template v-if="loadingDetail">
                    <v-skeleton-loader type="article"></v-skeleton-loader>
                </template>
                <template v-else>
                    <ticket-detail-view
                        ref="detailView"
                        :edited-item="displayTicket"
                        :status="status"
                        :current-user-id="user && user.id_usuario != null ? user.id_usuario : null"
                        :read-only-ticket-detail="readOnly || isAgenciaEmpregoReadonly"
                        :lock-dispatcher-status-to-readonly="false"
                        :lock-task-status-dialog="false"
                        :can-edit-inline-task-status="true"
                        :allow-dispatcher-close-task="false"
                        :equipes="equipes"
                        :usuarios-por-equipe="usuariosPorEquipe"
                        :tipos-tickets="tiposTickets"
                        :familias-atendimento="familiasAtendimento"
                        :tipos-atendimento="tiposAtendimento"
                        :servicos="servicos"
                        :tipos-solicitacao="tiposSolicitacao"
                        :modulos-sistema="modulosSistema"
                        :checklist-selecionado="checklistSelecionado"
                        :checklist-item="checklistItemForDisplay"
                        :checklist-valores-campos="checklistValoresCamposForDisplay"
                        :checklist-valores-edited-by="checklistValoresEditedByForDisplay"
                        :checklist-nome="checklistNome"
                        :equipamentos-display-text="equipamentosDisplayText"
                        :veiculos-display-text="veiculosDisplayText"
                        :comentarios="comentarios"
                        :edited-comentario="editedComentario"
                        :usuario-atual="user"
                        :loading-checklist-details="loadingChecklistDefinition"
                        :loading-comentario="loadingComentario"
                        :is-ticket-closed="isTicketClosed"
                        :token="token"
                        :show-status-change="false"
                        :show-user-assignment="false"
                        :show-team="false"
                        :edit-checklist="editChecklist"
                        :show-status="true"
                        :show-schedule-edit="canEditSchedule === true"
                        :show-checklist-section="true"
                        :show-comments="true"
                        :visible-detail-fields="visibleDetailFieldsForDetail"
                        :show-status-atribuido="true"
                        :show-status-dispatcher="true"
                        :show-status-atribuido-change="true"
                        :show-status-dispatcher-change="true"
                        :empresa="empresa"
                        :vehicle-equipment-pairs="vehicleEquipmentPairs"
                        @update:vehicleEquipmentPairs="onVehicleEquipmentPairsUpdate"
                        :equipamentos-filtrados-por-empresa="equipamentosFiltradosPorEmpresa"
                        :veiculos-filtrados-por-empresa="veiculosFiltradosPorEmpresa"
                        :loading-equipamentos="loadingEquipamentos"
                        :loading-veiculos="loadingVeiculos"
                        :get-equipamento-display-text-by-id="getEquipamentoDisplayTextById"
                        :initial-detail-tab="initialDetailTab"
                        :assist-status-dt-inicio="assistStatusDtInicio"
                        :show-tasks-tab-status-cta="showTasksTabStatusCta"
                        :hide-internal-observation="hideInternalObservation"
                        :pode-ver-historico="podeVerHistorico"
                        :hide-precandidatos-write-actions="hidePrecandidatosWriteActions"
                        v-on="$listeners"
                        @update:comment="editedComentario.corpo = $event"
                        @update:commentInterno="editedComentario.interno = $event"
                        @send-comment="onSendComment"
                    />
                </template>
            </v-card-text>
            <v-card-text v-else class="pa-3 text-center grey--text">
                {{ $t('ticket.loading') || 'Carregando...' }}
            </v-card-text>
        </v-card>
        <v-dialog v-model="discardDialog" max-width="420">
            <v-card>
                <v-card-title class="text-h6">{{ $t('ticket.unsavedChangesTitle') }}</v-card-title>
                <v-card-text>{{ $t('ticket.unsavedChangesText') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="discardDialog = false">{{ $t('ticket.keepEditing') }}</v-btn>
                    <v-btn text color="error" @click="confirmDiscard">{{ $t('ticket.discardChanges') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '../../utils/eventBus';
import { normalizeGetTicketEquipamentosResponse } from '@/utils/ticketEquipamentosResponse';
import TicketDetailView from './TicketDetailView.vue';

export default {
    name: 'TicketDetailDrawer',
    components: { TicketDetailView },
    props: {
        value: { type: Boolean, default: false },
        ticket: { type: Object, default: null },
        ticketId: { type: [Number, String], default: null },
        token: { type: String, default: '' },
        user: { type: Object, default: null },
        /** tickets colaborador vs dispatcher — controls which status editors are shown */
        dataSource: { type: String, default: '' },
        status: { type: Array, default: () => [] },
        visibleDetailFields: { type: Array, default: null },
        editChecklist: { type: Boolean, default: false },
        /** Toolbar: cancelar ticket (parent opens dialog and calls API). */
        showCloseTicketButton: { type: Boolean, default: false },
        /** Toolbar: concluir ticket (dispatcher only; parent handles request-close-ticket). */
        showConcludeTicketButton: { type: Boolean, default: false },
        /** Toolbar: imprimir ticket (parent opens the print preview via print-ticket). */
        showPrintButton: { type: Boolean, default: false },
        /** Hide internal observation in ticket detail (cliente / criados). */
        hideInternalObservation: { type: Boolean, default: false },
        podeVerHistorico: { type: Boolean, default: false },
        hidePrecandidatosWriteActions: { type: Boolean, default: false },
        /** Somente visualização (ex.: relatórios): desabilita edições no detalhe do ticket. */
        readOnly: { type: Boolean, default: false },
        initialDetailTab: { type: String, default: null },
        assistStatusDtInicio: { type: [Date, String, Number], default: null },
        showTasksTabStatusCta: { type: Boolean, default: false },
        /** Wizard (ticket-assist): kit/equip id to merge into vehicle–equipment selection so it matches v-autocomplete items */
        assistPrefillEquipamentoId: { type: [Number, String], default: null },
        /** Edição de data/hora de agendamento: true/false sobrepõe ticket_campos_detalhe (schedule_edit); null segue a configuração. */
        canEditSchedule: { type: Boolean, default: null },
        /** Toolbar: salvar as alterações do ticket (parent acumula as edições e grava no evento save). */
        showSaveButton: { type: Boolean, default: false },
        hasPendingChanges: { type: Boolean, default: false },
        saving: { type: Boolean, default: false }
    },
    data() {
        return {
            comentarios: [],
            editedComentario: { corpo: '', interno: false },
            loadingDetail: false,
            loadingComentario: false,
            detailEquipamentos: [],
            detailEquipamentosKit: [],
            detailVeiculos: [],
            checklistData: null,
            fullDetailTicket: null,
            familiasAtendimento: [],
            todasFamilias: [],
            tiposTickets: [],
            tiposAtendimento: [],
            servicos: [],
            tiposSolicitacao: [],
            modulosSistema: [],
            equipes: [],
            usuariosPorEquipe: [],
            checklistSelecionado: null,
            vehicleEquipmentPairs: [],
            equipamentosFiltradosPorEmpresa: [],
            veiculosFiltradosPorEmpresa: [],
            loadingEquipamentos: false,
            loadingVeiculos: false,
            equipamentos: [],
            kits: [],
            /** True while getchecklistequipamentoporid is in flight (checklist tab would be blank without this). */
            loadingChecklistDefinition: false,
            /** After checklist save: open technician change-status dialog once detail is visible again (avoids unmount during load). */
            openChangeStatusAfterDetailLoad: false,
            discardDialog: false
        };
    },
    computed: {
        isAgenciaEmpregoReadonly() {
            const u = this.user;
            return !!(u && u.perfil_usuario === 'AgenciaEmprego');
        },
        /** Ensure status fields/editors are not hidden by ticket_campos_detalhe. */
        visibleDetailFieldsForDetail() {
            const v = this.visibleDetailFields;
            if (!v || !Array.isArray(v)) return null;
            const add = ['status_atribuido', 'status_dispatcher', 'status_atribuido_change', 'status_dispatcher_change', 'dt_finalizacao'];
            const out = v.slice();
            add.forEach((k) => {
                if (out.indexOf(k) === -1) out.push(k);
            });
            if (this.canEditSchedule === true && out.indexOf('schedule_edit') === -1) out.push('schedule_edit');
            if (this.canEditSchedule === false) return out.filter((k) => k !== 'schedule_edit');
            return out;
        },
        ticketDisplayKey() {
            const d = this.displayTicket;
            if (!d || d.id_ticket == null || d.id_ticket === '') return '';
            return d.cod_ticket || `#${d.id_ticket}`;
        },
        displayTicket() {
            const f = this.fullDetailTicket || {};
            const t = this.ticket || {};
            // Parent overlays full for pending edits; restore full when list/stub sent null/undefined (e.g. assist flow).
            const base = { ...f, ...t };
            const restoreFromFull = [
                'id_checklist_equipamento',
                'ids_checklists_servico',
                'servico_nomes_checklists',
                'nome_checklist_extenso',
                'id_empresa',
                'id_servico',
                'id_familia_atendimento',
                'id_tipo_atendimento',
                'id_tipo_ticket',
                'suporte_sistema_id_modulo',
                'suporte_sistema_nome_modulo',
                'suporte_sistema_categoria_modulo',
                'email_recuperacao_usuario_inclusao',
                'login_usuario_inclusao',
                'solicitacao_candidatos_solicitante_email',
                'solicitacao_candidatos_nome_agencia',
                'solicitacao_candidatos_id_agencia'
            ];
            restoreFromFull.forEach((k) => {
                if ((t[k] === null || t[k] === undefined) && f[k] != null && f[k] !== '') {
                    base[k] = f[k];
                }
            });
            // Parent ticket (list / debounced save) often lacks id_equipes or sends [] while full detail has teams from DB.
            if (f.id_ticket) {
                const userExplicitTeamClear =
                    Object.prototype.hasOwnProperty.call(t, 'id_equipe') &&
                    (t.id_equipe === null || t.id_equipe === '' || t.id_equipe === undefined);
                const stubHasNoTeams =
                    t.id_equipes === undefined ||
                    t.id_equipes === null ||
                    t.id_equipes === '' ||
                    (Array.isArray(t.id_equipes) && t.id_equipes.length === 0);
                const fullHasTeams =
                    (typeof f.id_equipes === 'string' && f.id_equipes.trim() !== '') ||
                    (Array.isArray(f.id_equipes) && f.id_equipes.length > 0) ||
                    (typeof f.id_equipes === 'number' && Number.isFinite(f.id_equipes));
                if (!userExplicitTeamClear && stubHasNoTeams && fullHasTeams) {
                    base.id_equipes = f.id_equipes;
                    if (
                        f.nomes_equipes != null &&
                        f.nomes_equipes !== '' &&
                        (t.nomes_equipes === undefined || t.nomes_equipes === null || t.nomes_equipes === '')
                    ) {
                        base.nomes_equipes = f.nomes_equipes;
                    }
                }
                const stubHasNoIdEquipe =
                    t.id_equipe === undefined || t.id_equipe === null || t.id_equipe === '';
                if (!userExplicitTeamClear && stubHasNoIdEquipe && f.id_equipe != null && f.id_equipe !== '') {
                    base.id_equipe = f.id_equipe;
                }
            }
            return {
                ...base,
                equipamentos_associados: (base.equipamentos_associados && base.equipamentos_associados.length) ? base.equipamentos_associados : (this.detailEquipamentos || []),
                equipamentos_do_kit: (base.equipamentos_do_kit && base.equipamentos_do_kit.length) ? base.equipamentos_do_kit : (this.detailEquipamentosKit || []),
                veiculos_associados: (base.veiculos_associados && base.veiculos_associados.length) ? base.veiculos_associados : (this.detailVeiculos || [])
            };
        },
        checklistNome() {
            return this.checklistSelecionado && this.checklistSelecionado.nome_checklist ? this.checklistSelecionado.nome_checklist : null;
        },
        checklistItemForDisplay() {
            const d = this.checklistData;
            if (!d) return {};
            const equipamentos = d.equipamentos || [];
            const nome_equipamento = equipamentos.length > 0
                ? equipamentos.map(e => e.nome_equipamento || e.cod_equipamento || e.codigo_qrcode || `ID: ${e.id_equipamento}`).join(', ')
                : null;
            return {
                id_resposta: d.id_resposta,
                id_checklist_equipamento: d.id_checklist_equipamento,
                observacoes: d.observacoes,
                dt_realizacao: d.dt_realizacao,
                dt_realizacao_extenso: d.dt_realizacao_extenso,
                dt_alteracao_extenso: d.dt_inclusao_extenso,
                nome_equipamento,
                fotos_extra: Array.isArray(d.fotos_extra) ? d.fotos_extra : []
            };
        },
        checklistValoresCamposForDisplay() {
            const d = this.checklistData;
            if (!d || !d.valores || !Array.isArray(d.valores)) return {};
            const out = {};
            d.valores.forEach(v => {
                const val = v.valor;
                out[v.id_campo] = v.tipo === 'Checkbox' ? (val === 'true') : val;
            });
            return out;
        },
        checklistValoresEditedByForDisplay() {
            const d = this.checklistData;
            if (!d || !d.valores || !Array.isArray(d.valores)) return {};
            const out = {};
            d.valores.forEach(v => {
                if (v.nome_usuario_alteracao || v.dt_alteracao_extenso) {
                    out[v.id_campo] = { nome_usuario_alteracao: v.nome_usuario_alteracao, dt_alteracao_extenso: v.dt_alteracao_extenso };
                }
            });
            return out;
        },
        equipamentosDisplayText() {
            if (!this.displayTicket.equipamentos_associados || !this.displayTicket.equipamentos_associados.length) return '-';
            return this.displayTicket.equipamentos_associados
                .map(e => e.codigo_qrcode || e.cod_equipamento || e.nome_equipamento || `ID: ${e.id_equipamento}`)
                .join(', ');
        },
        veiculosDisplayText() {
            if (!this.displayTicket.veiculos_associados || !this.displayTicket.veiculos_associados.length) return '-';
            return this.displayTicket.veiculos_associados
                .map(v => v.placa || v.identificacao || `ID: ${v.id_veiculo}`)
                .join(', ');
        },
        isTicketClosed() {
            const t = this.displayTicket;
            if (!t) return false;
            return t.finalizado === 1 || t.finalizado === '1' || t.finalizado_extenso === 'Sim';
        },
        /** id_empresa from ticket's client; used to load equipamentos and veiculos for the detail view. */
        empresa() {
            const ticket = this.fullDetailTicket || this.ticket;
            if (ticket && ticket.id_empresa != null) return ticket.id_empresa;
            if (this.detailVeiculos && this.detailVeiculos.length > 0 && this.detailVeiculos[0].id_empresa != null) return this.detailVeiculos[0].id_empresa;
            if (this.detailEquipamentos && this.detailEquipamentos.length > 0) {
                const first = this.detailEquipamentos[0];
                return first.id_empresa != null ? first.id_empresa : null;
            }
            return null;
        }
    },
    watch: {
        value(open) {
            if (open && (this.ticket?.id_ticket || this.ticketId)) {
                this.loadFullDetail();
                this.loadComentarios();
            } else if (!open) {
                this.openChangeStatusAfterDetailLoad = false;
                this.detailEquipamentos = [];
                this.detailEquipamentosKit = [];
                this.detailVeiculos = [];
                this.checklistData = null;
                this.checklistSelecionado = null;
                this.fullDetailTicket = null;
                this.familiasAtendimento = [];
                this.tiposTickets = [];
                this.tiposAtendimento = [];
                this.servicos = [];
                this.tiposSolicitacao = [];
                this.modulosSistema = [];
                this.equipes = [];
                this.usuariosPorEquipe = [];
                this.vehicleEquipmentPairs = [];
                this.equipamentosFiltradosPorEmpresa = [];
                this.veiculosFiltradosPorEmpresa = [];
                this.equipamentos = [];
                this.kits = [];
                this.loadingChecklistDefinition = false;
            }
        },
        // Triagem no card: a família/tipo de atendimento/tipo editados recarregam as listas em cascata.
        'displayTicket.id_familia_atendimento'(newVal, oldVal) {
            if (this.loadingDetail || newVal === oldVal) return;
            this.loadTiposAtendimento();
        },
        'displayTicket.id_tipo_atendimento'(newVal, oldVal) {
            if (this.loadingDetail || newVal === oldVal) return;
            this.loadServicos();
        },
        'displayTicket.id_tipo_ticket'(newVal, oldVal) {
            if (this.loadingDetail || newVal === oldVal) return;
            this.loadFamilias();
            this.loadTiposAtendimento();
            this.loadClassificacaoListas();
        },
        ticket: {
            handler(newVal, oldVal) {
                const newId = newVal && newVal.id_ticket;
                const oldId = oldVal && oldVal.id_ticket;
                if (newId !== oldId && this.value && newId) {
                    this.loadFullDetail();
                    this.loadComentarios();
                }
            },
            deep: false
        }
    },
    methods: {
        onDialogInput(open) {
            if (open) {
                this.$emit('input', true);
                return;
            }
            this.requestClose();
        },
        /** Fechar com alterações pendentes pede confirmação para descartá-las. */
        requestClose() {
            if (this.showSaveButton && this.hasPendingChanges) {
                this.discardDialog = true;
                return;
            }
            this.$emit('input', false);
        },
        confirmDiscard() {
            this.discardDialog = false;
            this.$emit('discard-changes');
            this.$emit('input', false);
        },
        emitRequestFinalizeTicket(mode) {
            const t = this.displayTicket;
            if (!t || !t.id_ticket) return;
            if (mode !== 'conclude' && mode !== 'cancel') return;
            this.$emit('request-close-ticket', { ...t, finalizeMode: mode });
        },
        onVehicleEquipmentPairsUpdate(val) {
            this.vehicleEquipmentPairs = val;
            this.$emit('vehicle-equipment-pairs-change', val);
        },
        requestOpenChangeStatusWhenDetailLoaded() {
            this.openChangeStatusAfterDetailLoad = true;
        },
        openChangeStatusAtribuidoDialog() {
            if (this.isTicketClosed) return;
            const v = this.$refs.detailView;
            if (v && typeof v.openChangeStatusAtribuidoDialog === 'function') {
                v.openChangeStatusAtribuidoDialog();
            }
        },
        async loadFullDetail() {
            const id = this.ticket?.id_ticket || this.ticketId;
            if (!id || !this.token) {
                this.openChangeStatusAfterDetailLoad = false;
                return;
            }
            this.loadingDetail = true;
            this.detailEquipamentos = [];
            this.detailEquipamentosKit = [];
            this.detailVeiculos = [];
            this.checklistData = null;
            this.fullDetailTicket = null;
            this.familiasAtendimento = [];
            this.tiposTickets = [];
            this.tiposAtendimento = [];
            this.servicos = [];
            this.equipes = [];
            this.usuariosPorEquipe = [];
            this.loadingChecklistDefinition = false;
            try {
                const [fullRes, equipRes, veicRes, tiposTicketsRes, familiasRes, equipesRes] = await Promise.all([
                    apiController.request(apiController.endpoints.getTicketbyid, { params: { id }, silent: true }),
                    apiController.request(apiController.endpoints.getTicketequipamentos, { params: { id }, silent: true }),
                    apiController.request(apiController.endpoints.getTicketveiculos, { params: { id }, silent: true }),
                    apiController.request(apiController.endpoints.getallTipostickets, { silent: true }),
                    apiController.request(apiController.endpoints.getFamiliasatendimento, { silent: true }),
                    apiController.request(apiController.endpoints.getallEquipes, { silent: true })
                ]);
                const full = fullRes.data;
                if (full && !full.erro) {
                    this.fullDetailTicket = full;
                }
                const eqNorm = normalizeGetTicketEquipamentosResponse(equipRes.data);
                this.detailEquipamentos = eqNorm.equipamentos;
                this.detailEquipamentosKit = eqNorm.equipamentos_do_kit;
                this.detailVeiculos = Array.isArray(veicRes.data) ? veicRes.data : [];
                this.tiposTickets = Array.isArray(tiposTicketsRes.data) ? tiposTicketsRes.data : [];
                this.todasFamilias = Array.isArray(familiasRes.data) ? familiasRes.data : [];
                this.familiasAtendimento = this.todasFamilias;
                this.equipes = Array.isArray(equipesRes.data) ? equipesRes.data : [];
                this.checklistSelecionado = null;
                this.checklistData = null;
                const fullTicket = full && !full.erro ? full : null;
                const idChkResponse = fullTicket && fullTicket.id_checklist_equipamento;
                const checklistRes = idChkResponse
                    ? await apiController.request(apiController.endpoints.getChecklistequipamentorespostaporticket, { params: { id_ticket: id }, query: { id_checklist_equipamento: idChkResponse }, silent: true })
                    : { data: { dados: null } };
                const dados = checklistRes.data && checklistRes.data.dados;
                this.checklistData = dados && !checklistRes.data.erro ? dados : null;
                const idChecklistDef =
                    (this.checklistData && this.checklistData.id_checklist_equipamento) ||
                    (fullTicket && fullTicket.id_checklist_equipamento);
                if (idChecklistDef) {
                    this.loadingChecklistDefinition = true;
                    try {
                        const defRes = await apiController.request(apiController.endpoints.getChecklistequipamentoporid, { params: { id: idChecklistDef }, silent: true });
                        if (defRes.data && !defRes.data.erro) {
                            this.checklistSelecionado = defRes.data;
                        }
                    } catch (eDef) {
                        // erro ao carregar definição do checklist — ignorado
                    } finally {
                        this.loadingChecklistDefinition = false;
                    }
                }

                const ticket = this.fullDetailTicket || this.ticket || {};
                const idFamilia = ticket.id_familia_atendimento;
                const idTipoAtendimento = ticket.id_tipo_atendimento;
                const idTipoTicket = ticket.id_tipo_ticket;
                const [tiposAtendimentoRes, servicosRes, usuariosList] = await Promise.all([
                    idFamilia
                        ? apiController.request(apiController.endpoints.getTiposatendimento, { query: { id_familia_atendimento: idFamilia, ...(idTipoTicket ? { id_tipo_ticket: idTipoTicket } : {}) }, silent: true })
                        : Promise.resolve({ data: [] }),
                    idTipoAtendimento
                        ? apiController.request(apiController.endpoints.getServicos, { query: { id_tipo_atendimento: idTipoAtendimento }, silent: true })
                        : Promise.resolve({ data: [] }),
                    this.fetchUsuariosPorEquipes(this.equipes)
                ]);
                this.tiposAtendimento = Array.isArray(tiposAtendimentoRes.data) ? tiposAtendimentoRes.data : [];
                this.servicos = Array.isArray(servicosRes.data) ? servicosRes.data : [];
                this.usuariosPorEquipe = Array.isArray(usuariosList) ? usuariosList : [];
                await Promise.all([this.loadFamilias(), this.loadClassificacaoListas()]);
                const idEmpresa = this.empresa;
                if (idEmpresa) {
                    this.fetchEquipamentosVeiculosPorEmpresa(idEmpresa).then(() => {
                        this.buildVehicleEquipmentPairsFromDetail();
                    });
                } else {
                    this.buildVehicleEquipmentPairsFromDetail();
                }
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            } finally {
                this.loadingDetail = false;
                if (this.openChangeStatusAfterDetailLoad) {
                    this.openChangeStatusAfterDetailLoad = false;
                    this.$nextTick(() => this.openChangeStatusAtribuidoDialog());
                }
            }
        },
        /**
         * Famílias com atendimento cadastrado para o tipo do ticket: é o que o cadastro permite, e
         * evita escolher uma família cuja lista de tipos de atendimento viria vazia. Ticket antigo
         * com família fora do tipo a mantém na lista, para não sumir do campo.
         */
        async loadFamilias() {
            const t = this.displayTicket || {};
            if (!t.id_tipo_ticket) {
                this.familiasAtendimento = this.todasFamilias;
                return;
            }
            try {
                const res = await apiController.request(apiController.endpoints.getFamiliasatendimento, {
                    query: { id_tipo_ticket: t.id_tipo_ticket },
                    silent: true
                });
                const lista = Array.isArray(res.data) ? res.data : [];
                const atual = this.todasFamilias.find((f) => Number(f.id_familia_atendimento) === Number(t.id_familia_atendimento));
                const temAtual = !atual || lista.some((f) => Number(f.id_familia_atendimento) === Number(atual.id_familia_atendimento));
                this.familiasAtendimento = temAtual ? lista : [...lista, atual];
            } catch (e) {
                this.familiasAtendimento = this.todasFamilias;
            }
        },
        loadTiposAtendimento() {
            const t = this.displayTicket || {};
            if (!t.id_familia_atendimento) {
                this.tiposAtendimento = [];
                return Promise.resolve();
            }
            const query = { id_familia_atendimento: t.id_familia_atendimento };
            if (t.id_tipo_ticket) query.id_tipo_ticket = t.id_tipo_ticket;
            return apiController.request(apiController.endpoints.getTiposatendimento, { query, silent: true })
                .then((res) => { this.tiposAtendimento = Array.isArray(res.data) ? res.data : []; })
                .catch(() => { this.tiposAtendimento = []; });
        },
        loadServicos() {
            const t = this.displayTicket || {};
            if (!t.id_tipo_atendimento) {
                this.servicos = [];
                return Promise.resolve();
            }
            return apiController.request(apiController.endpoints.getServicos, { query: { id_tipo_atendimento: t.id_tipo_atendimento }, silent: true })
                .then((res) => { this.servicos = Array.isArray(res.data) ? res.data : []; })
                .catch(() => { this.servicos = []; });
        },
        /**
         * Tipos de solicitação (Tipo de problema no suporte) e módulos do sistema, editados no card.
         * Usa os tipos ligados ao tipo do ticket; sem nenhum, os sem tipo de ticket; sem nenhum, todos.
         */
        async loadClassificacaoListas() {
            const t = this.displayTicket || {};
            const idTipo = t.id_tipo_ticket != null && t.id_tipo_ticket !== '' ? Number(t.id_tipo_ticket) : null;
            const tipoRow = (this.tiposTickets || []).find((x) => Number(x.id_tipo_ticket) === idTipo);
            const nomeTipo = String((tipoRow && tipoRow.nome_tipo_ticket) || t.tipo_ticket_extenso || '').trim().toUpperCase();
            const isSuporte = nomeTipo === 'SUPORTE SISTEMA (MERUSTRAKER)';
            try {
                const [tiposRes, modulosRes] = await Promise.all([
                    apiController.request(apiController.endpoints.getTipossolicitacao, { silent: true }),
                    isSuporte
                        ? apiController.request(apiController.endpoints.getallModulossistema, { silent: true })
                        : Promise.resolve({ data: [] })
                ]);
                const todos = (Array.isArray(tiposRes.data) ? tiposRes.data : [])
                    .filter((x) => x.ativo === undefined || x.ativo === 1 || x.ativo === true);
                const doTipo = todos.filter((x) => x.id_tipo_ticket != null && Number(x.id_tipo_ticket) === idTipo);
                const semTipo = todos.filter((x) => x.id_tipo_ticket == null);
                this.tiposSolicitacao = doTipo.length ? doTipo : (semTipo.length ? semTipo : todos);
                this.modulosSistema = Array.isArray(modulosRes.data) ? modulosRes.data : [];
            } catch (e) {
                this.tiposSolicitacao = [];
                this.modulosSistema = [];
            }
        },
        async fetchEquipamentosVeiculosPorEmpresa(idEmpresa) {
            if (!idEmpresa) return;
            this.loadingEquipamentos = true;
            this.loadingVeiculos = true;
            const filterAndFormatEquipamentos = () => {
                const equipamentosFiltrados = (this.equipamentos || [])
                    .filter(e => parseInt(e.id_empresa) === parseInt(idEmpresa) && (e.ativo === 1 || e.ativo_extenso === 'Sim'))
                    .map(e => {
                        const idEq = Number(e.id_equipamento);
                        return {
                            ...e,
                            id_equipamento: Number.isFinite(idEq) ? idEq : e.id_equipamento,
                            tipo: 'equipamento',
                            displayText: e.codigo_qrcode || e.cod_equipamento || e.nome_equipamento || `ID: ${e.id_equipamento}`
                        };
                    });
                const kitsFiltrados = (this.kits || [])
                    .filter(k => parseInt(k.id_empresa) === parseInt(idEmpresa) && (k.ativo === 1 || k.ativo_extenso === 'Sim'))
                    .map(k => {
                        const idKit = Number(k.id_kit);
                        return {
                            ...k,
                            id_equipamento: Number.isFinite(idKit) ? idKit : k.id_kit,
                            nome_equipamento: k.nome_kit || k.cod_kit,
                            cod_equipamento: k.cod_kit,
                            id_tipo_kit: k.id_tipo_kit,
                            tipo: 'kit',
                            displayText: `[KIT] ${k.nome_kit || k.cod_kit} (${k.cod_kit})`
                        };
                    });
                this.equipamentosFiltradosPorEmpresa = [...equipamentosFiltrados, ...kitsFiltrados];
                this.loadingEquipamentos = false;
            };
            const loadEquipamentos = async () => {
                const r = await apiController.request(apiController.endpoints.getallEquipamentos, { silent: true });
                this.equipamentos = r.data || [];
            };
            const loadKits = async () => {
                const r = await apiController.request(apiController.endpoints.getallKits, { silent: true });
                this.kits = r.data || [];
            };
            const loadVeiculos = async () => {
                try {
                    const r = await apiController.request(apiController.endpoints.getVeiculoporempresa, { params: { id: idEmpresa }, silent: true });
                    this.veiculosFiltradosPorEmpresa = (r.data || [])
                        .filter(v => v.ativo === 1 || v.ativo_extenso === 'Sim')
                        .map(v => ({ ...v, displayText: v.numero_ordem ? `${v.numero_ordem} (${v.placa || ''})` : (v.placa || `ID: ${v.id_veiculo}`) }));
                    this.loadingVeiculos = false;
                } catch (e) {
                    this.loadingVeiculos = false;
                }
            };
            try {
                await Promise.all([loadEquipamentos(), loadKits(), loadVeiculos()]);
                filterAndFormatEquipamentos();
            } catch (e) {
                this.loadingEquipamentos = false;
                EventBus.$emit('show-error', { error: e });
            }
        },
        buildVehicleEquipmentPairsFromDetail() {
            const veiculos = this.detailVeiculos || [];
            const equipamentos = this.detailEquipamentos || [];
            const equipamentoIds = [];
            const seen = new Set();
            const pushEquipId = (raw) => {
                if (raw == null || raw === '') return;
                let id;
                if (typeof raw === 'string' && (raw.startsWith('te_') || raw.startsWith('tk_'))) {
                    id = raw;
                } else {
                    const n = Number(raw);
                    id = Number.isFinite(n) ? n : raw;
                }
                const key = typeof id === 'number' ? `n:${id}` : `s:${String(id)}`;
                if (seen.has(key)) return;
                seen.add(key);
                equipamentoIds.push(id);
            };
            (equipamentos || []).forEach(e => pushEquipId(e.id_equipamento != null ? e.id_equipamento : e.id_kit));
            const prefill = this.assistPrefillEquipamentoId;
            if (prefill != null && prefill !== '') {
                const n = Number(prefill);
                if (Number.isFinite(n) && n > 0) pushEquipId(n);
            }
            if (veiculos.length > 0) {
                this.vehicleEquipmentPairs = [{ id_veiculo: veiculos[0].id_veiculo, equipamentos: equipamentoIds }];
            } else if (equipamentoIds.length > 0) {
                this.vehicleEquipmentPairs = [{ id_veiculo: null, equipamentos: equipamentoIds }];
            } else {
                this.vehicleEquipmentPairs = [{ id_veiculo: null, equipamentos: [] }];
            }
        },
        getEquipamentoDisplayTextById(id) {
            const list = this.equipamentosFiltradosPorEmpresa || [];
            const want = Number(id);
            const item = list.find(e => {
                const eq = e.id_equipamento != null ? e.id_equipamento : e.id_kit;
                return (Number.isFinite(want) && Number(eq) === want) || eq == id;
            });
            return item ? (item.displayText || item.codigo_qrcode || item.cod_equipamento || item.nome_equipamento || `ID: ${id}`) : `ID: ${id}`;
        },
        async fetchUsuariosPorEquipes(equipesList) {
            if (!this.token || !Array.isArray(equipesList) || equipesList.length === 0) {
                return [];
            }
            const responses = await Promise.all(
                equipesList.map(async (e) => {
                    try {
                        return await apiController.request(apiController.endpoints.getUsuariosporequipe, { params: { id: e.id_equipe != null ? e.id_equipe : e }, silent: true });
                    } catch (err) {
                        return { data: [] };
                    }
                })
            );
            const seen = new Set();
            const merged = [];
            responses.forEach(res => {
                const list = Array.isArray(res.data) ? res.data : [];
                list.forEach(u => {
                    const uid = u.id_usuario != null ? u.id_usuario : u.id;
                    if (uid != null && !seen.has(uid)) {
                        seen.add(uid);
                        merged.push(u);
                    }
                });
            });
            return merged;
        },
        loadComentarios() {
            const id = this.ticket?.id_ticket || this.ticketId;
            if (!id) return;
            this.comentarios = [];
            apiController.request(apiController.endpoints.getComentariosbyticketid, {
                params: { id },
                onSuccess: (res) => { this.comentarios = res.data || []; },
            });
        },
        async onSendComment() {
            const id = this.ticket?.id_ticket || this.ticketId;
            if (!id || !this.editedComentario.corpo || !this.user) return;
            this.loadingComentario = true;
            const payload = {
                id_ticket: id,
                corpo: this.editedComentario.corpo,
                id_usuario_comentario: this.user.id_usuario,
                interno: this.editedComentario.interno ? 1 : 0
            };
            try {
                await apiController.request(apiController.endpoints.insertComentario, { data: payload, silent: true });
                this.editedComentario.corpo = '';
                this.editedComentario.interno = false;
                this.loadComentarios();
                this.$emit('updated');
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            } finally {
                this.loadingComentario = false;
            }
        }
    }
};
</script>

<style scoped>
.ticket-detail-dialog-card {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}
.dialog-content {
    max-height: 90vh;
    flex: 1 1 auto;
    min-height: 0;
}
</style>
